import { useEffect, useMemo, useRef, useState } from 'react'
import { OpeningKind, SlotCandidate } from './slotCandidates'
import { SCHEDULE_CANDIDATE_CONTACTS_QUERY } from './graphql'
import { BAND_CONTACTS_QUERY } from 'app/components/action-center/graphql'
import { ACTION_QUEUE_BY_ID, hygieneDaysUntilDue } from 'app/components/action-center/queueRegistry'
import {
  TREATMENT_BANDS,
  TreatmentBand,
  bandSearchTerms,
  daysSincePlanned,
} from 'app/components/action-center/treatmentBands'
import { useQueueValueStats } from 'app/components/action-center/useQueueValueStats'
import { ActionCenterBandContactsQuery, ScheduleSlotCandidatesQuery } from 'app/generated/gql/graphql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * The hygiene queues an opening draws from.
 *
 * Both overdue bands, and the ones coming due inside the month. A chair open
 * next Tuesday is worth offering to somebody due a week later just as much as
 * to somebody a year late: the practice would rather book them early than call
 * them again in a fortnight.
 */
const HYGIENE_QUEUES = [`due-for-hygiene`, `hygiene-long-past-due`, `hygiene-coming-due`] as const

/** How deep to read each queue. The panel shows twelve; the rest are a count. */
const QUEUE_LIMIT = 100

type HygieneHit = ScheduleSlotCandidatesQuery[`contacts`][`hits`][number]
type TreatmentHit = ActionCenterBandContactsQuery[`contacts`][`hits`][number]

/**
 * One queue, as far as it was read.
 *
 * `total` is what the practice actually has in that queue and `contacts` is the
 * page in hand, which is the smaller of the two whenever a queue runs deeper
 * than `QUEUE_LIMIT`. Keeping both is what lets the panel say "2,326 waiting"
 * over a hundred rows rather than reporting its own read depth as the cohort.
 */
type QueueRead =
  | { source: `hygiene`; label: string; contacts: HygieneHit[]; total: number; failed: boolean }
  | { source: `treatment`; label: string; contacts: TreatmentHit[]; total: number; failed: boolean }

export type SlotCandidatePool = {
  candidates: SlotCandidate[]
  /** How many each queue holds, by cohort label, whatever depth was read. */
  totals: Map<string, number>
  loading: boolean
  /**
   * The candidate fetch failed. An empty panel would otherwise read as "nobody
   * to call", which on a surface whose whole job is to name somebody is the
   * same lie the calendar itself is built to avoid.
   */
  failed: boolean
}

/**
 * One hygiene recall queue at this location.
 *
 * The queue's own search and sort, from the registry the Action Center runs on,
 * so a band here holds the same people it holds there.
 */
async function readHygieneQueue(queueId: string, locationId: string): Promise<QueueRead> {
  const queue = ACTION_QUEUE_BY_ID.get(queueId)
  if (!queue) return { source: `hygiene`, label: ``, contacts: [], total: 0, failed: false }
  const label = queue.shortName ?? queue.name
  const search = [...queue.searchTerms({ needsActionDateEnabled: false }), `locationId:"${locationId}"`].join(` `)
  try {
    const result = await gqlQuery<ScheduleSlotCandidatesQuery>(SCHEDULE_CANDIDATE_CONTACTS_QUERY, {
      search,
      pagination: { limit: QUEUE_LIMIT },
      sort: queue.sort({ needsActionDateEnabled: false }),
    })
    return {
      source: `hygiene`,
      label,
      contacts: result.error ? [] : (result.data?.contacts?.hits ?? []),
      total: result.data?.contacts?.total ?? 0,
      failed: Boolean(result.error),
    }
  } catch (err) {
    /**
     * A rejected request is one empty queue, not an empty panel.
     *
     * `gqlQuery` reports a GraphQL error on the result it returns, but a
     * transport failure rejects, and a rejection escaping this reader would
     * take its siblings with it: the whole batch would fail and the panel would
     * say nobody is waiting anywhere, which is the one lie it exists to avoid.
     * This cohort is dropped and the rest of the panel still answers.
     */
    logError(`Schedule week could not read the ${label} queue`, err)
    return { source: `hygiene`, label, contacts: [], total: 0, failed: true }
  }
}

/**
 * One band of unscheduled treatment at this location, from the practice's own
 * records.
 *
 * `bandSearchTerms` and `BAND_CONTACTS_QUERY` are the Action Center board's
 * own, so a band offered for a chair here is the same cohort the board works,
 * counted the same way. The board's other three reads — procedures, plans,
 * appointments — are not repeated: those build the card's breakdown, and this
 * panel names a case and prices it rather than drawing it.
 *
 * Sorted by value, unlike the board, which opens on the oldest presentation. An
 * opening is an hour of capacity and the question here is what can fill it, so
 * reading the top of the money first means the shortlist is the band's best
 * cases even when the band runs deeper than one page.
 */
async function readTreatmentBand(band: TreatmentBand, locationId: string): Promise<QueueRead> {
  const search = [...bandSearchTerms(band.id), `locationId:"${locationId}"`].join(` `)
  try {
    const result = await gqlQuery<ActionCenterBandContactsQuery>(BAND_CONTACTS_QUERY, {
      search,
      pagination: { limit: QUEUE_LIMIT },
      sort: { field: `unbookedTreatmentValue`, direction: `DESC` },
    })
    return {
      source: `treatment`,
      // The band's full name, not its short one. On the board the short form sits
      // under a heading that says what the days measure; here it would read
      // `0 to 30 days` beside a hygiene cohort, with nothing to say of what.
      label: band.name,
      contacts: result.error ? [] : (result.data?.contacts?.hits ?? []),
      total: result.data?.contacts?.total ?? 0,
      failed: Boolean(result.error),
    }
  } catch (err) {
    // One band short, not a panel with nothing in it. See `readHygieneQueue`.
    logError(`Schedule week could not read the ${band.name} band`, err)
    return { source: `treatment`, label: band.name, contacts: [], total: 0, failed: true }
  }
}

function hygieneCandidate(contact: HygieneHit, queueName: string, value?: number): SlotCandidate {
  // `hygieneDaysUntilDue` reads one field, `lastHygieneDate`, which this
  // selection carries; the wider contact type it is declared against is not
  // needed to answer the question.
  const untilDue = hygieneDaysUntilDue(contact as Parameters<typeof hygieneDaysUntilDue>[0])
  return {
    id: `hygiene-${contact.id}`,
    name: contact.name || `Unnamed patient`,
    source: `hygiene`,
    sourceLabel: queueName,
    value,
    // Overdue is a positive wait; somebody not yet due has not waited at all.
    waitingDays: untilDue == null ? undefined : Math.max(0, -untilDue),
    contactId: contact.id,
    phone: contact.cellPhone ?? undefined,
    detail:
      untilDue == null
        ? undefined
        : untilDue < 0
          ? `${Math.abs(untilDue)} days overdue for a cleaning`
          : `Due for a cleaning in ${untilDue} days`,
  }
}

function treatmentCandidate(contact: TreatmentHit, bandName: string): SlotCandidate {
  // 490 of the practice's people with treatment outstanding carry no plan date
  // at all, and the open-ended band is written to catch them rather than lose
  // them. They are a real case with a real figure; only the age is unknown, so
  // the row says that instead of inventing a number of days.
  const days = contact.lastTreatmentPlanDate ? daysSincePlanned(contact.lastTreatmentPlanDate) : undefined
  return {
    id: `treatment-${contact.id}`,
    name: contact.name || `Unnamed patient`,
    source: `treatment`,
    sourceLabel: bandName,
    /**
     * The practice's own rollup of treatment that is pending with no live
     * appointment. It is the figure the band search itself selects and sorts
     * on, so the panel is consistent with the cohort it drew. The board's card
     * headline re-sums the classified procedures instead and the two can
     * differ; the breakdown that would explain a difference is on the card,
     * which is one click away on the patient.
     */
    value: contact.unbookedTreatmentValue ?? undefined,
    waitingDays: days,
    contactId: contact.id,
    phone: contact.cellPhone ?? undefined,
    detail: days == null ? `No presentation date on the plan` : `${days} days since the plan was presented`,
  }
}

/**
 * People to put in an opening, from the queues the practice already works.
 *
 * Only the side of the population the opening can actually take is fetched:
 * asking for a thousand hygiene contacts to fill the dentist's consult chair
 * would be a page of records nobody can be offered.
 */
export function useSlotCandidates(locationId?: string, kind: OpeningKind = `unknown`): SlotCandidatePool {
  const workspace = useWorkspace()
  const locationIds = useMemo(() => (locationId ? [locationId] : []), [locationId])
  const { stats } = useQueueValueStats(workspace?.id, locationIds)
  const [reads, setReads] = useState<QueueRead[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const run = useRef(0)

  const wantsHygiene = kind === `hygiene` || kind === `either` || kind === `unknown`
  const wantsTreatment = kind === `treatment` || kind === `either` || kind === `unknown`

  useEffect(() => {
    run.current += 1
    const token = run.current
    if (!locationId || (!wantsHygiene && !wantsTreatment)) {
      setReads([])
      setLoading(false)
      setFailed(false)
      return
    }
    setLoading(true)
    setFailed(false)
    bg(
      Promise.all([
        ...(wantsHygiene ? HYGIENE_QUEUES.map((queueId) => readHygieneQueue(queueId, locationId)) : []),
        ...(wantsTreatment ? TREATMENT_BANDS.map((band) => readTreatmentBand(band, locationId)) : []),
      ])
        .then((results) => {
          if (token !== run.current) return
          setReads(results)
          // One queue failing leaves the panel thinner; all of them failing
          // leaves it unable to answer, which is the case worth saying out loud.
          setFailed(results.length > 0 && results.every((result) => result.failed))
          setLoading(false)
        })
        // A backstop, not the failure path: each reader catches its own, so a
        // single queue rejecting no longer costs the panel its siblings. Only
        // something outside a reader can land here.
        .catch((err) => {
          if (token !== run.current) return
          logError(`Schedule week could not load the people for this opening`, err)
          setReads([])
          setFailed(true)
          setLoading(false)
        }),
    )
  }, [locationId, wantsHygiene, wantsTreatment])

  const candidates = useMemo(() => {
    // The same patient can sit in two hygiene windows at once, and the first
    // queue to claim them wins so nobody is offered twice. Somebody overdue for
    // a cleaning *and* carrying an unbooked case is two different offers for two
    // different chairs, and their ids differ by source, so both of those survive.
    const seen = new Set<string>()
    return reads
      .flatMap((read) =>
        read.source === `hygiene`
          ? read.contacts.map((contact) => hygieneCandidate(contact, read.label, stats.avgProductionPerVisit))
          : read.contacts.map((contact) => treatmentCandidate(contact, read.label)),
      )
      .filter((candidate) => {
        if (seen.has(candidate.id)) return false
        seen.add(candidate.id)
        return true
      })
  }, [reads, stats.avgProductionPerVisit])

  const totals = useMemo(() => new Map(reads.map((read) => [read.label, read.total])), [reads])

  return { candidates, totals, loading, failed }
}
